import React, { useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  useGetDairyCowTypes,
  useGetFootprintingDairyHerdDetails,
  useGetFootprintingDairyHerdSize,
  useMutateFootprintingDairyHerdDetails,
  useMutateFootprintingDairyHerdSize,
  useTranslation,
} from "utils";

import { FootprintingDairyHerdSize } from "models";

import { Card, Flex, Form, FormInstance, InputImageCard, PageSpin } from "components";

interface CowTypeSelectionScreenProps {
  year: number;
  farmId: string;
  primaryDataId?: string;
  existingHerdSize?: FootprintingDairyHerdSize[];
  onCountsChange?: (counts: Record<string, number>) => void;
  onComplete?: () => void;
  readOnly?: boolean;
}

const CowTypeSelectionScreen = ({ primaryDataId, existingHerdSize = [], onCountsChange, readOnly }: CowTypeSelectionScreenProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const [cowCounts, setCowCounts] = useState<Record<string, number>>({});

  const cowCountsRef = useRef(cowCounts);
  const existingHerdRef = useRef(existingHerdSize);

  useEffect(() => {
    cowCountsRef.current = cowCounts;
  }, [cowCounts]);

  useEffect(() => {
    existingHerdRef.current = existingHerdSize;
  }, [existingHerdSize]);

  const { data: cowTypes, isLoading: cowTypesLoading } = useGetDairyCowTypes({ enabled: true });
  const { mutate: saveHerdSize } = useMutateFootprintingDairyHerdSize(primaryDataId || "");

  const isLoading = cowTypesLoading;

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        const herdData = Object.entries(cowCountsRef.current)
          .filter(([_, count]) => count > 0)
          .map(([cowTypeId, cowCount]) => {
            const existing = existingHerdRef.current.find((h) => h.cowTypeId === cowTypeId);
            return {
              cowTypeId,
              cowCount,
              ...(existing && { id: existing.id }),
            };
          });

        if (herdData.length > 0) {
          saveHerdSize(herdData);
        }
      }
    },
    [isLoading, saveHerdSize, readOnly, primaryDataId]
  );

  useEffect(() => {
    if (existingHerdSize && existingHerdSize.length > 0) {
      const initialCounts = existingHerdSize.reduce(
        (acc, herd) => {
          acc[herd.cowTypeId] = herd.cowCount;
          return acc;
        },
        {} as Record<string, number>
      );
      setCowCounts(initialCounts);
      onCountsChange?.(initialCounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingHerdSize]);

  const handleCowCountChange = (cowTypeId: string, value: number | null) => {
    const newCounts = {
      ...cowCounts,
      [cowTypeId]: value || 0,
    };
    setCowCounts(newCounts);
    onCountsChange?.(newCounts);
  };

  if (cowTypesLoading) {
    return <PageSpin />;
  }

  if (!cowTypes || cowTypes.length === 0) {
    return null;
  }

  return (
    <Flex vertical align="center" gap={0} className="margin">
      {cowTypes.map((cowType) => (
        <InputImageCard
          key={cowType.id}
          title={t(`dairy:cowTypes.${cowType.translationSlug}`)}
          onChange={(value) => handleCowCountChange(cowType.id, value)}
          image={{
            imageSize: 48,
            imageAlt: `${cowType.name} image`,
            imageSrc: `https://klimpublic.s3.eu-central-1.amazonaws.com/${cowType.translationSlug}.png`,
          }}
          value={cowCounts[cowType.id] || null}
          disabled={readOnly}
        />
      ))}
    </Flex>
  );
};

export const HerdSizeForm = ({ primaryDataId, readOnly, farmId, year }: CowTypeSelectionScreenProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);
  const queryClient = useQueryClient();

  const [calvesAmount, setCalvesAmount] = useState<number | null>(null);
  const [cowCounts, setCowCounts] = useState<Record<string, number>>({});

  const { data: herdDetails, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "", {
    enabled: !!primaryDataId,
  });

  const { data: existingHerdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", {
    enabled: !!primaryDataId,
  });

  const { mutate: mutateHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      calvesAmount,
    },
    primaryDataId
  );

  const totalHerdSize = useMemo(() => {
    if (!cowCounts) return 0;
    const cowTotal = Object.values(cowCounts).reduce((sum, count) => sum + (count || 0), 0);
    return cowTotal + (calvesAmount || 0);
  }, [cowCounts, calvesAmount]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId) {
        mutateHerdDetails();
        queryClient.invalidateQueries({ queryKey: ["footprintingDairyHerdSize", primaryDataId] });
      }
    },
    [isLoading, mutateHerdDetails, primaryDataId, readOnly, queryClient]
  );

  useEffect(() => {
    if (herdDetails) {
      setCalvesAmount(herdDetails.calvesAmount ?? null);
      formRef.current?.setFieldsValue({
        calvesAmount: herdDetails.calvesAmount ?? null,
      });

      const fieldsToValidate = herdDetails.calvesAmount !== null && herdDetails.calvesAmount !== undefined;

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyHerdSizeForm.title")}</h1>
      <Card dataTestId="footprinting-farm-soil-form" className="margin-bottom-100 padding-200">
        <Flex vertical gap={8}>
          <h4>{t("dairyHerdSizeForm.herdSizeQuestion")}</h4>
          <p className="margin-top-0 margin-bottom-100 grey-600-text paragraph-m">{t("dairyHerdSizeForm.herdSizeQuestionDescription")}</p>
        </Flex>
        <Form.Item>
          <CowTypeSelectionScreen
            year={year}
            farmId={farmId}
            primaryDataId={primaryDataId || ""}
            existingHerdSize={existingHerdSize || []}
            onCountsChange={setCowCounts}
            readOnly={readOnly}
          />
        </Form.Item>
      </Card>

      <Card className="padding-200 margin-top-100">
        <Form ref={formRef}>
          <Flex vertical>
            <Flex vertical gap={8}>
              <h4>{t("dairyHerdSizeForm.herdSizeAmountOfCalvesQuestion")}</h4>
              <p className="margin-top-0 margin-bottom-100 grey-600-text paragraph-m">{t("dairyHerdSizeForm.herdSizeAmountOfCalvesQuestionDescription")}</p>
            </Flex>
            <Form.Item>
              <InputImageCard
                title={t("dairyHerdSizeForm.question.herdSizeAmountOfCalvesInput")}
                onChange={(value) => setCalvesAmount(value)}
                image={{
                  imageSize: 48,
                  imageAlt: "Calves image",
                  imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/calve.png",
                }}
                value={calvesAmount}
                disabled={readOnly}
              />
            </Form.Item>
          </Flex>
        </Form>
      </Card>

      <Card className="padding-200 margin-top-100">
        <Flex className="dairy-display-information-card flex-center padding-100">
          <p className="paragraph-s grey-600-text margin-0 margin-bottom-25">{t("dairyHerdSizeForm.herdSizeDisplayTitle")}</p>
          <h5 className="center" key={totalHerdSize}>
            {totalHerdSize} {t("dairyHerdSizeForm.herdSizeDisplayUnit")}
          </h5>
        </Flex>
      </Card>
    </div>
  );
};
